import type { VendorRequestStanding } from './api'

// The request step before due diligence (release 3.3), as the screens read it.
//
// # WHY THIS FILE EXISTS
//
// The order is now Requested, a TPRM lead approves the request, due diligence,
// the risk approval, Active. The server holds the rules (packages/core/domain/
// request.go) and refuses a questionnaire for a vendor whose request is not
// approved. A screen that offered the Send button anyway would be a dead button:
// pressed, refused. So the send screen, the register and the next step read the
// same rule here first, in the server's own words, and say what to do instead.
//
// The gate below is a mirror, not a second rule: the shared fixture
// packages/core/domain/testdata/request_rules.json is run against it by
// test/request-step.test.mjs and against the server's by request_rules_test.go,
// so the two cannot say different things. Who may approve or reject a request
// is never decided here: the lifecycle read answers it for the reader
// (VendorRequestStanding.approve and .reject), and requestFooter only draws it.

export type VendorStanding = { status: string; requestApproved?: boolean; statutory?: { complete: boolean } }

// Whether the request counts as approved. An API older than release 3.3 sends
// no field, and every vendor that API knew was on the register before the
// request step existed (migration 0108 records them as approved), so absent is
// approved.
export function requestApproved(v: Pick<VendorStanding, 'requestApproved'>): boolean {
  return v.requestApproved !== false
}

// A vendor still being requested: DRAFT, and nobody has approved the request.
export function isRequested(v: VendorStanding): boolean {
  return v.status === 'DRAFT' && !requestApproved(v)
}

export const MSG_REQUEST_NOT_APPROVED = 'Approve the vendor request before sending due diligence.'

export type GateRefusal = { code: string; message: string }

// The gate every questionnaire path asks (domain.CheckDueDiligence), in its
// order and its words. Null means a questionnaire may go.
export function dueDiligenceRefusal(v: VendorStanding, reason = ''): GateRefusal | null {
  switch (v.status) {
    case 'REJECTED':
      return { code: 'VENDOR_REJECTED', message: 'This vendor was rejected. A rejected vendor is not sent due diligence.' }
    case 'OFFBOARDED':
      return { code: 'VENDOR_OFFBOARDED', message: 'This vendor has been offboarded. An offboarded vendor is not sent due diligence.' }
  }
  if (!requestApproved(v)) return { code: 'REQUEST_NOT_APPROVED', message: MSG_REQUEST_NOT_APPROVED }
  switch (v.status) {
    case 'PENDING_APPROVAL':
      return {
        code: 'VENDOR_AWAITING_APPROVAL',
        message: 'This vendor is awaiting approval on the due diligence it has. Return it to draft to ask for more.',
      }
    case 'SUSPENDED':
      if (reason.trim() === '') return { code: 'SUSPENDED_NEEDS_REASON', message: 'This vendor is suspended. Give a reason to send it due diligence.' }
  }
  return null
}

// Whether a reason would let the vendor through: only a suspension asks for
// one. The send screen and the bulk send's picker show the reason field for
// exactly these.
export function needsReason(v: VendorStanding): boolean {
  return dueDiligenceRefusal(v, '')?.code === 'SUSPENDED_NEEDS_REASON'
}

// Who a requested vendor is waiting on, in the words the register row, the
// next step and the send screen all use.
export const WAITING_ON_LEAD = 'Waiting on TPRM lead'

// The next step a register row shows under its stage, where the row has one to
// show (C4 of the 12 Oct plan): a requested vendor waits on the lead.
export function registerNextStep(v: VendorStanding): string | null {
  return isRequested(v) ? 'Request: ' + WAITING_ON_LEAD.charAt(0).toLowerCase() + WAITING_ON_LEAD.slice(1) : null
}

// The way forward when the gate says no, for the screen that was about to send.
// A requested vendor is approved by the lead (the reader, if they may);
// awaiting approval is returned to draft on the Profile; the closed ones have
// no way forward and say so.
export type WayForward =
  | { kind: 'approve-request' }
  | { kind: 'wait'; who: string }
  | { kind: 'open'; label: string; href: string }
  | { kind: 'reason' }
  | { kind: 'none' }

export function wayForward(v: VendorStanding & { id: string }, refusal: GateRefusal, mayApproveRequest: boolean): WayForward {
  switch (refusal.code) {
    case 'REQUEST_NOT_APPROVED':
      return mayApproveRequest ? { kind: 'approve-request' } : { kind: 'wait', who: 'the TPRM lead' }
    case 'VENDOR_AWAITING_APPROVAL':
      return { kind: 'open', label: 'Open approval', href: '#/vendor/' + v.id + '/full/profile' }
    case 'SUSPENDED_NEEDS_REASON':
      return { kind: 'reason' }
  }
  return { kind: 'none' }
}

// ------------------------------------------------------- the drawer's footer

export type FooterButton = { disabled: boolean; why?: string }
export type RequestFooter = { approve: FooterButton; reject: FooterButton; note?: string }

// The Approve request drawer's two buttons, from the lifecycle read and what
// the reader has typed. Approve needs no reason; Reject needs one, and is not
// pressed into a 422 for the lack of it. While a decision is being sent both
// wait. A reader the read gives no decision to (they do not hold
// vendor.request_approve, or the API is older) is shown neither as live.
export function requestFooter(state: VendorRequestStanding | null | undefined, reason: string, busy: boolean): RequestFooter {
  if (!state || !state.approve || !state.reject) {
    const why = 'Only the TPRM lead approves or rejects a vendor request.'
    return { approve: { disabled: true, why }, reject: { disabled: true, why }, note: WAITING_ON_LEAD + '.' }
  }
  if (busy) return { approve: { disabled: true }, reject: { disabled: true } }
  const approve: FooterButton = state.approve.allowed ? { disabled: false } : { disabled: true, why: state.approve.blockedBecause }
  let reject: FooterButton = state.reject.allowed ? { disabled: false } : { disabled: true, why: state.reject.blockedBecause }
  if (!reject.disabled && reason.trim() === '') reject = { disabled: true, why: 'Say why the request is rejected. It is what the person who asked will be shown.' }
  // One sentence under the buttons when the rule stops both for one reason
  // (the tenant asks for a second person and the reader raised it).
  const note = approve.disabled && reject.disabled && approve.why && approve.why === state.reject.blockedBecause ? approve.why : undefined
  return { approve, reject, note }
}

// ------------------------------------------------- statutory gaps, said early

// C7 of the 12 Oct plan: the statutory gaps (a PAN, a GSTIN, the legal form)
// block submitting a vendor for approval and not due diligence, and nothing on
// the vendor said so until the submit was refused. On the Requested and the due
// diligence stages (both DRAFT) the record now says it in one quiet line.
export const STATUTORY_NOTE = 'Statutory details missing: needed before approval'

export function statutoryNote(v: VendorStanding): string | null {
  return v.status === 'DRAFT' && v.statutory != null && !v.statutory.complete ? STATUTORY_NOTE : null
}

// ------------------------------------------------------ the bulk send's picker

// Which vendors a bulk send may name (C5 of the 12 Oct plan): the ones the gate
// lets through, and a suspended one, which goes with the campaign's reason. A
// vendor still being requested, awaiting approval, rejected or offboarded is
// not offered, because the server would refuse the whole send naming it.
export function mayPick(v: VendorStanding): boolean {
  const r = dueDiligenceRefusal(v, '')
  return r == null || r.code === 'SUSPENDED_NEEDS_REASON'
}

// Whether a bulk send naming these vendors needs a reason: one of them is
// suspended.
export function pickNeedsReason(vs: VendorStanding[]): boolean {
  return vs.some(needsReason)
}
